
import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { getSafeClient } from "@/integrations/supabase/safeClient";
import ProfileReviews from "@/components/ProfileReviews";

interface RequestReviewSectionProps {
  buyerId: string;
  buyerName: string;
  requestId: string;
  currentUserId: string | null;
}

const RequestReviewSection = ({ buyerId, buyerName, requestId, currentUserId }: RequestReviewSectionProps) => {
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [ratingValue, setRatingValue] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const { toast } = useToast();

  const handleSubmitReview = async () => {
    if (!currentUserId) {
      toast({
        title: "Error",
        description: "You must be logged in to leave a review",
        variant: "destructive",
      });
      return;
    }

    try {
      const supabaseClient = await getSafeClient();
      const { error } = await supabaseClient
        .from('profile_reviews')
        .insert({
          profile_id: buyerId,
          reviewer_id: currentUserId,
          request_id: requestId,
          rating: ratingValue,
          text: reviewText,
          review_type: 'seller_to_buyer'
        });

      if (error) throw error;

      toast({
        title: "Review submitted",
        description: "Thank you for your feedback about this buyer!",
      });

      setRatingValue(5);
      setReviewText("");
      setShowReviewDialog(false);
    } catch (err) {
      console.error("Error submitting review:", err);
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="mt-12">
      <h2 className="text-2xl font-bold mb-6">Buyer Reputation</h2>
      <div className="flex justify-between items-center mb-4">
        <p className="text-gray-600">
          Reviews help other sellers know what to expect when working with this buyer.
        </p>
        {currentUserId && currentUserId !== buyerId && (
          <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
            <DialogTrigger asChild>
              <Button>Leave Review for Buyer</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Review {buyerName}</DialogTitle>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="flex justify-center space-x-1">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <Star
                      key={value}
                      className={`h-8 w-8 cursor-pointer ${
                        value <= ratingValue
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-300"
                      }`}
                      onClick={() => setRatingValue(value)}
                    />
                  ))}
                </div>
                <Textarea
                  placeholder="How was your experience with this buyer?"
                  className="min-h-[150px]"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                />
              </div>
              <Button onClick={handleSubmitReview}>Submit Review</Button>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <ProfileReviews profileId={buyerId} profileName={buyerName} />
    </div>
  );
};

export default RequestReviewSection;
